import { Button } from '@/components/common/Button';
import { DATE_YMD_HMS_12H_FORMAT, NULL_VALUE_DISPLAY, REVIEW_STATUS, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { LANGUAGES_SUPPORTED, REVIEW_STATUS_TYPE } from '@/types/enums';
import { loadURLPreview } from '@/utils/browsers';
import { formatDateTime } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { cn } from '@/utils/utils';
import classNames from 'classnames';
import { CheckIcon, StarIcon, Trash2Icon } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

export default observer(function ViewReviewPage() {
    // hooks
    const { id } = useParams();
    const { t, i18n: { language } } = useTranslation();
    const navigate = useNavigate();

    // store
    const {
        reviewStore: { reviewDetail, getReviewDetail, publishReview, deleteReview },
        modalStore: { showAlertModal, modalWaringAlertOptions }
    } = useStore();

    // lifecycle
    useEffect(() => {
        onFetchData();
    }, []);

    useEffect(() => {
        if (reviewDetail && reviewDetail.images.length > 0) {
            reviewDetail.images.forEach((item, index) => loadURLPreview(item, `preview-image-${index}`));
        }
    }, [reviewDetail]);

    // function
    const onFetchData = () => {
        if (id) {
            getReviewDetail(id);
        }
    };

    const handleClickPublished = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_published_review'),
            onSave: async () => {
                const res = await flowResult(publishReview(id ?? ''));
                if (res && res.ok) {
                    toastify('success', t('messages.update_success'));
                    navigate(ROUTES.reviewManagement.href);
                }
            }
        }));
    };

    const handleClickDelete = async () => {
        showAlertModal(modalWaringAlertOptions({
            hideIconCenter: true,
            size: 'lg',
            content: t('messages.confirm_delete_review'),
            onSave: async () => {
                const res = await flowResult(deleteReview(id ?? ''));
                if (res && res.ok) {
                    toastify('success', t('messages.update_success'));
                    navigate(ROUTES.reviewManagement.href);
                }
            }
        }));
    };

    const handleGoBack = () => {
        navigate(ROUTES.reviewManagement.href);
    };

    return (
        <div className='pb-4 h-full'>
            <div className='bg-white p-4 rounded'>
                <div className='text-active font-medium'>{t('words_title.shop_info')}</div>
                <div className='p-4'>
                    <div className='grid grid-cols-2 gap-x-24 gap-y-2 justify-between'>
                        <div className='flex gap-4 mt-4'>
                            <div className='text-text-4 w-1/5'>{t('words_title.shop_name')}</div>
                            {
                                reviewDetail?.shop?.isDeleted ?
                                    (
                                        <div className='w-4/5'>{reviewDetail.shop.name}</div>
                                    ) :
                                    (
                                        <Link to={`/shops/${reviewDetail?.shop.code}`} target='blank' rel='noopener noreferrer' className='w-4/5 underline text-link-1 cursor-pointer'>{reviewDetail?.shop?.name}</Link>
                                    )
                            }
                        </div>
                        <ReviewDetailItem label={t('words_title.phone_number')} content={reviewDetail?.shop?.phoneNumber ?? ''} />
                        <div className='flex gap-4 mt-4 col-span-2'>
                            <div className='text-text-4 w-[calc(10%_-_0.8rem)]'>{t('words_title.shop_address')}</div>
                            <div className='w-[90%] whitespace-pre-line'>
                                {reviewDetail?.shop?.address ?? ''}
                            </div>
                        </div>
                        <div className='border border-t border-border-1 col-span-2 mt-4' />
                        <ReviewDetailItem label={t('words_title.shop_owner')} content={reviewDetail?.shop?.user?.fullName ?? ''} />
                        <ReviewDetailItem label={t('words_title.phone_number')} content={reviewDetail?.shop?.user?.phoneNumber ?? ''} />
                    </div>
                </div>
            </div>
            <div className='bg-white p-4 rounded mt-4'>
                <div className='text-active font-medium'>{t('words_title.review_info')}</div>
                <div className='p-4'>
                    <div className='grid grid-cols-2 gap-x-24 gap-y-2 justify-between'>
                        <div className='flex gap-4 mt-4'>
                            <div className='text-text-4 w-1/5'>{t('words_title.reviewer')}</div>
                            {
                                reviewDetail?.reviewer?.isDeleted ?
                                    (
                                        <div className='w-4/5'>{reviewDetail.reviewer?.fullName}</div>
                                    ) :
                                    (
                                        <Link to={`/users/${reviewDetail?.reviewer?.code}`} target='blank' rel='noopener noreferrer' className='w-4/5 underline text-link-1 cursor-pointer'>{reviewDetail?.reviewer?.fullName}</Link>
                                    )
                            }
                        </div>
                        <ReviewDetailItem label={t('words_title.phone_number')} content={reviewDetail?.reviewer?.phoneNumber ?? ''} />
                        <ReviewDetailItem label={t('words_title.email')} content={reviewDetail?.reviewer?.email ?? ''} />
                        <ReviewDetailItem label={t('words_title.city')} content={language === LANGUAGES_SUPPORTED.Vietnamese ? reviewDetail?.reviewer?.province?.fullName ?? '' : reviewDetail?.reviewer?.province?.fullNameEn ?? ''} />
                        {
                            reviewDetail?.hasBookAppointment && (
                                <div className='text-[0.8125rem] font-medium text-destructive whitespace-pre-line col-span-2 mt-4'>
                                    {t('sentences.already_have_booked')}
                                </div>
                            )
                        }
                        <div className='border border-t border-border-1 col-span-2 mt-4' />
                        <div className='flex items-center gap-4 mt-4'>
                            <div className='text-text-4 w-1/5'>{t('words_title.review_rating')}</div>
                            <div className='w-4/5'>
                                <div className='flex items-center gap-1'>
                                    {reviewDetail?.serviceQuality && Array.from({ length: 5 }, (_, index) => {
                                        const currentIndex = index + 1;
                                        return (
                                            <StarIcon
                                                key={index}
                                                className={classNames(currentIndex <= reviewDetail.serviceQuality ? 'fill-star-1 text-star-1' : 'text-star-1')}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <ReviewDetailItem label={t('words_title.created_date')} content={reviewDetail?.createdAt ? formatDateTime(reviewDetail?.createdAt, DATE_YMD_HMS_12H_FORMAT) ?? '' : ''} />
                        <div className='flex gap-4 mt-4 col-span-2'>
                            <div className='text-text-4 w-[calc(10%_-_0.8rem)]'>{t('words_title.comment')}</div>
                            <div className='w-[90%] whitespace-pre-line'>
                                {reviewDetail?.comment ?
                                    reviewDetail.comment :
                                    (
                                        reviewDetail?.images && reviewDetail.images?.length > 0 ? '' : NULL_VALUE_DISPLAY
                                    )}
                            </div>
                        </div>
                        {
                            reviewDetail?.images && reviewDetail.images?.length > 0 && (
                                <div className='flex gap-4 col-span-2 mt-4'>
                                    <div className='w-[calc(10%_-_0.8rem)]'></div>
                                    <div className='w-[90%] grid grid-cols-5 gap-2'>
                                        {
                                            reviewDetail?.images.map((item, index) => (
                                                <img key={index} className='w-full h-44 object-cover rounded' id={`preview-image-${index}`} alt=''></img>
                                            ))
                                        }
                                    </div>
                                </div>
                            )
                        }
                        <div className='border border-t border-border-1 col-span-2 mt-4' />
                        <div className='flex items-center gap-4 mt-4'>
                            <div className='text-text-4 w-1/5'>{t('words_title.status')}</div>
                            <div className='w-4/5'>
                                {
                                    reviewDetail?.status && (
                                        <div className='px-4 py-1 bg-border-1 flex gap-2 items-center rounded-2xl max-w-fit'>
                                            <div className={classNames('rounded h-2 w-2', REVIEW_STATUS[reviewDetail?.status].color)}></div>
                                            {t(`words_title.${REVIEW_STATUS[reviewDetail?.status].key}`)}
                                        </div>
                                    )
                                }
                            </div>
                        </div>
                        {
                            reviewDetail?.status === REVIEW_STATUS_TYPE.RequestApproval && (
                                <>
                                    <ReviewDetailItem label={t('words_title.request_date')} content={formatDateTime(reviewDetail?.reportDatetime, DATE_YMD_HMS_12H_FORMAT) ?? ''} />
                                    <div className='flex gap-4 mt-4 col-span-2'>
                                        <div className='text-text-4 w-[calc(10%_-_0.8rem)]'>{t('words_title.reason')}</div>
                                        <div className='w-[90%] whitespace-pre-line'>{reviewDetail?.reportReason ?? ''}</div>
                                    </div>
                                </>
                            )
                        }
                    </div>
                </div>
            </div>
            <div className='bg-white mt-4 rounded p-4 flex justify-between items-center sticky z-10 bottom-0 left-0'>
                {
                    reviewDetail && reviewDetail?.status && reviewDetail.status === REVIEW_STATUS_TYPE.RequestApproval ?
                        (
                            <div className='flex items-center gap-2'>
                                <Button
                                    type='button'
                                    onClick={handleClickPublished}
                                    className='flex items-center justify-center px-4 py-5 bg-white min-w-40 gap-2 border border-accept-action text-accept-action'
                                >
                                    <CheckIcon className='w-5 h-5 text-accept-action' />
                                    {t('words_title.published_review')}
                                </Button>
                                <Button
                                    type='button'
                                    onClick={handleClickDelete}
                                    className='flex items-center justify-center px-4 py-5 bg-white min-w-40 gap-2 border border-delete-action text-delete-action'
                                >
                                    <Trash2Icon className='w-5 h-5 text-delete-action' />
                                    {t('words_title.delete_review')}
                                </Button>
                            </div>
                        ) :
                        <div></div>
                }
                <Button className='bg-text-5 rounded text-foreground' onClick={handleGoBack}>{t('words_title.back_to_review_lst')}</Button>
            </div>
        </div>
    );
});

interface ReviewDetailItemProps {
    label: string,
    content: string,
    classname?: string
}

const ReviewDetailItem = ({ label, content, classname }: ReviewDetailItemProps) => {
    return (
        <div className={cn('flex gap-4 mt-4', classname)}>
            <div className='text-text-4 w-1/5'>{label}</div>
            <div className='w-4/5'>{content}</div>
        </div>
    );
};
